import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props } from './componentEl-c8b73b10.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import { a as addSpacingClasses, p as paddingPropTypes, m as marginPropTypes } from './spacing-7e57ffb0.js';
import { s as setIcon } from './icons-fde0c050.js';
import { A as Avatar } from './Avatar-a7fa1853.js';
import Icon from './Icon.js';

function ListItem({
  componentEl: Component,
  avatar,
  children,
  className,
  padding,
  margin,
  icon,
  listStyle,
  ...otherProps
}) {
  let cssClasses = ['st_listitem'];
  if (listStyle) cssClasses.push(`st_listitem--${listStyle}`);
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses
  });
  let avatarProps = {};
  if (avatar) {
    avatarProps = {
      avatar: avatar.avatar,
      initials: avatar.initials,
      size: 'mlg',
      className: 'st_listitem__avatar'
    };
  }
  const listItemIcon = setIcon(Icon, icon, {
    className: 'st_listitem__icon'
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), listItemIcon, avatar && /*#__PURE__*/React.createElement(Avatar, avatarProps), children);
}
ListItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** props for optional avatar, if any, only accepts avatar and initials */
  avatar: PropTypes.shape({
    avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    initials: PropTypes.string
  }),
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** list style for list items, if any */
  listStyle: PropTypes.oneOf(['number', 'none', 'bullet']),
  /** optional icon for token. A screentone icon component, e.g. IconStar */
  icon: PropTypes.any,
  /** padding for list item, if any */
  padding: paddingPropTypes,
  /** margin for list item, if any */
  margin: marginPropTypes
};
ListItem.defaultProps = {
  componentEl: 'li',
  className: null,
  listStyle: null,
  margin: null,
  padding: null,
  icon: null,
  avatar: null
};
ListItem.displayName = 'List.Item';

function List({
  componentEl: Component,
  children,
  className,
  listStyle,
  margin,
  padding,
  itemPadding,
  itemMargin,
  ...otherProps
}) {
  let cssClasses = ['st_list'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  const childrenWithProps = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    const cloneParams = {};
    if (child.type.displayName === 'List.Item') {
      cloneParams.listStyle = child.props.listStyle || listStyle;
      cloneParams.padding = child.props.padding || itemPadding;
      cloneParams.margin = child.props.margin || itemMargin;
    }
    return /*#__PURE__*/React.cloneElement(child, cloneParams);
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), childrenWithProps);
}
List.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** list-style for list items, if any */
  listStyle: PropTypes.oneOf(['number', 'none', 'bullet']),
  /** margin for list, if any */
  margin: marginPropTypes,
  /** padding for list contents */
  padding: paddingPropTypes,
  /** default padding for all list items */
  itemPadding: paddingPropTypes,
  /** default margin for all list items */
  itemMargin: marginPropTypes
};
List.defaultProps = {
  componentEl: 'ul',
  listStyle: null,
  className: null,
  margin: {
    all: 'none'
  },
  padding: {
    all: 'none'
  },
  itemPadding: null,
  itemMargin: {
    vertical: 'sm'
  }
};
List.Item = ListItem;

export { List as L };
